import {
  hasAdequateHydration,
  isPhysicallyActive,
  sharedActivityFrequencyStep,
  sharedProteinIntakeStep,
  sharedSleepQualityStep,
  sharedStressLevelStep,
  sharedWaterIntakeStep,
} from '@/flow/shared/commonQuestions'
import { validateTextoLivre } from '@/flow/validators'
import type { Step } from '@/types'

// Helper to check user sex
const isMale = (ctx: { vars: Record<string, any> }) => ctx.vars.sexo === 'masculino'
const isFemale = (ctx: { vars: Record<string, any> }) => ctx.vars.sexo === 'feminino'

// Helper to check selected areas
const hasArea = (area: string) => (ctx: { vars: Record<string, any> }) => {
  const areas = ctx.vars.preocupacoes_cabelos_pele_unhas
  return Array.isArray(areas) && areas.includes(area)
}

// Helper to check specific concerns
const hasHairConcern = (concern: string) => (ctx: { vars: Record<string, any> }) => {
  const concerns = ctx.vars.preocupacoes_cabelo
  return Array.isArray(concerns) && concerns.includes(concern)
}

const hasFaceConcern = (concern: string) => (ctx: { vars: Record<string, any> }) => {
  const concerns = ctx.vars.preocupacoes_pele
  return Array.isArray(concerns) && concerns.includes(concern)
}

const hasBodyConcern = (concern: string) => (ctx: { vars: Record<string, any> }) => {
  const concerns = ctx.vars.preocupacoes_corpo
  return Array.isArray(concerns) && concerns.includes(concern)
}

const hasNailConcern = (concern: string) => (ctx: { vars: Record<string, any> }) => {
  const concerns = ctx.vars.preocupacoes_unhas
  return Array.isArray(concerns) && concerns.includes(concern)
}

const hasSelection = (key: string, value: string) => (ctx: { vars: Record<string, any> }) => {
  const selections = ctx.vars[key]
  return Array.isArray(selections) && selections.includes(value)
}

const hasHairLossConcern = (ctx: { vars: Record<string, any> }) => {
  const concerns = ctx.vars.preocupacoes_cabelo
  return (
    Array.isArray(concerns) &&
    concerns.some((c) => ['queda', 'afinamento', 'entradas', 'falhas', 'sem_volume'].includes(c))
  )
}

const hasHairTreatments = (ctx: { vars: Record<string, any> }) => {
  const treatments = ctx.vars.tratamentos_atuais_cabelo
  return Array.isArray(treatments) && treatments.length > 0
}

const hasSkinArea = (ctx: { vars: Record<string, any> }) =>
  hasArea('pele_rosto')(ctx) || hasArea('pele_corpo')(ctx)

const isValue = (key: string, value: string) => (ctx: { vars: Record<string, any> }) =>
  ctx.vars[key] === value

/** Objective-specific steps: Hair, Skin & Nails (cabelos_pele_unhas) */
export const hairSkinNailsSteps: Step[] = [


  // ========== FOCUS AREAS ==========
  {
    id: 'focus_areas',
    type: 'choice',
    inputKey: 'preocupacoes_cabelos_pele_unhas',
    multiple: true,
    minSelections: 1,
    content: 'Quais áreas você quer avaliar com mais atenção?',
    options: [
      { label: 'Cabelo', value: 'cabelo' },
      { label: 'Pele do rosto', value: 'pele_rosto' },
      { label: 'Pele do corpo', value: 'pele_corpo' },
      { label: 'Unhas', value: 'unhas' },
    ],
  },

  // ========== HAIR ==========
  {
    id: 'intro_hair',
    type: 'message',
    content: 'Vamos começar falando sobre o seu cabelo, {{nome}}.',
    condition: hasArea('cabelo'),
  },
  {
    id: 'hair_concerns',
    type: 'choice',
    inputKey: 'preocupacoes_cabelo',
    multiple: true,
    minSelections: 1,
    content: 'Quais são suas principais preocupações com o cabelo?',
    condition: hasArea('cabelo'),
    options: [
      { label: 'Queda de cabelo', value: 'queda' },
      { label: 'Afinamento dos fios', value: 'afinamento' },
      { label: 'Entradas / Linha frontal recuando', value: 'entradas' },
      { label: 'Falhas no couro cabeludo', value: 'falhas' },
      { label: 'Cabelo sem volume', value: 'sem_volume' },
      { label: 'Couro cabeludo oleoso', value: 'oleosidade' },
      { label: 'Caspa / Dermatite seborreica', value: 'caspa' },
      { label: 'Cabelo ressecado', value: 'ressecamento' },
      { label: 'Cabelo quebradiço', value: 'quebradico' },
      { label: 'Coceira ou sensibilidade no couro cabeludo', value: 'coceira' },
    ],
  },
  {
    id: 'hair_loss_duration',
    type: 'choice',
    inputKey: 'queda_duracao',
    content: 'Há quanto tempo você nota a queda de cabelo?',
    condition: (ctx) => hasArea('cabelo')(ctx) && hasHairConcern('queda')(ctx),
    options: [
      { label: 'Menos de 6 meses', value: 'menos_6_meses' },
      { label: '6 meses a 1 ano', value: '6_meses_1_ano' },
      { label: '1 a 3 anos', value: '1_3_anos' },
      { label: 'Mais de 3 anos', value: 'mais_3_anos' },
    ],
  },
  {
    id: 'hair_loss_intensity',
    type: 'choice',
    inputKey: 'queda_intensidade',
    content: 'Como você classificaria a intensidade da queda?',
    condition: (ctx) => hasArea('cabelo')(ctx) && hasHairConcern('queda')(ctx),
    options: [
      { label: 'Leve (alguns fios a mais que o normal)', value: 'leve' },
      { label: 'Moderada (bastante fios no travesseiro/chuveiro)', value: 'moderada' },
      { label: 'Severa (tufos de cabelo caindo)', value: 'severa' },
    ],
  },
  {
    id: 'hair_loss_pattern_male',
    type: 'choice',
    inputKey: 'padrao_queda',
    content: 'Onde você percebe mais a queda?',
    condition: (ctx) => hasArea('cabelo')(ctx) && hasHairConcern('queda')(ctx) && isMale(ctx),
    multiple: true,
    minSelections: 1,
    options: [
      { label: 'Entradas (testa lateral)', value: 'entradas' },
      { label: 'Coroa (topo da cabeça)', value: 'coroa' },
      { label: 'Entradas e coroa', value: 'entradas_coroa' },
      { label: 'Difusa (cabelo todo)', value: 'difusa' },
    ],
  },
  {
    id: 'hair_loss_pattern_female',
    type: 'choice',
    inputKey: 'padrao_queda',
    content: 'Onde você percebe mais a queda?',
    condition: (ctx) => hasArea('cabelo')(ctx) && hasHairConcern('queda')(ctx) && isFemale(ctx),
    multiple: true,
    minSelections: 1,
    options: [
      { label: 'Risca central (alargamento)', value: 'risca_central' },
      { label: 'Topo da cabeça', value: 'topo' },
      { label: 'Difusa (cabelo todo)', value: 'difusa' },
      { label: 'Frontal (linha do cabelo)', value: 'frontal' },
    ],
  },
  {
    id: 'hair_loss_pattern_neutral',
    type: 'choice',
    inputKey: 'padrao_queda',
    content: 'Onde você percebe mais a queda?',
    condition: (ctx) =>
      hasArea('cabelo')(ctx) && hasHairConcern('queda')(ctx) && !isMale(ctx) && !isFemale(ctx),
    multiple: true,
    minSelections: 1,
    options: [
      { label: 'Entradas (testa lateral)', value: 'entradas' },
      { label: 'Topo da cabeça', value: 'topo' },
      { label: 'Difusa (cabelo todo)', value: 'difusa' },
      { label: 'Linha frontal', value: 'frontal' },
    ],
  },
  {
    id: 'hair_loss_when',
    type: 'choice',
    inputKey: 'queda_quando',
    content: 'Em quais momentos você percebe mais queda?',
    condition: (ctx) => hasArea('cabelo')(ctx) && hasHairConcern('queda')(ctx),
    options: [
      { label: 'No banho', value: 'banho' },
      { label: 'Ao pentear', value: 'pentear' },
      { label: 'Durante o dia', value: 'dia' },
      { label: 'Em todos os momentos', value: 'sempre' },
    ],
  },
  {
    id: 'thinning_when',
    type: 'choice',
    inputKey: 'afinamento_quando',
    content: 'Quando você notou o afinamento ou perda de volume?',
    condition: (ctx) =>
      hasArea('cabelo')(ctx) &&
      (hasHairConcern('afinamento')(ctx) || hasHairConcern('sem_volume')(ctx)),
    options: [
      { label: 'Sempre tive fios finos', value: 'sempre' },
      { label: 'Começou recentemente', value: 'recente' },
      { label: 'Vem piorando aos poucos', value: 'progressivo' },
    ],
  },
  {
    id: 'gaps_location',
    type: 'choice',
    inputKey: 'falhas_localizacao',
    content: 'Onde aparecem as falhas?',
    condition: (ctx) => hasArea('cabelo')(ctx) && hasHairConcern('falhas')(ctx),
    multiple: true,
    minSelections: 1,
    options: [
      { label: 'Topo da cabeça', value: 'topo' },
      { label: 'Lateral', value: 'lateral' },
      { label: 'Nuca', value: 'nuca' },
      { label: 'Linha frontal', value: 'frontal' },
      { label: 'Barba', value: 'barba' },
    ],
  },
  {
    id: 'scalp_oiliness',
    type: 'choice',
    inputKey: 'oleosidade_couro',
    content: 'Como está a oleosidade do seu couro cabeludo?',
    condition: (ctx) => hasArea('cabelo')(ctx) && hasHairConcern('oleosidade')(ctx),
    options: [
      { label: 'Normal', value: 'normal' },
      { label: 'Oleoso', value: 'oleoso' },
      { label: 'Muito oleoso', value: 'muito_oleoso' },
    ],
  },
  {
    id: 'dandruff_type',
    type: 'choice',
    inputKey: 'caspa_tipo',
    content: 'Que tipo de caspa você tem?',
    condition: (ctx) => hasArea('cabelo')(ctx) && hasHairConcern('caspa')(ctx),
    options: [
      { label: 'Caspa seca (flocos brancos)', value: 'seca' },
      { label: 'Caspa oleosa (placas amareladas)', value: 'oleosa' },
      { label: 'Dermatite seborreica (vermelhidão e coceira)', value: 'dermatite' },
      { label: 'Não sei ao certo', value: 'nao_sei' },
    ],
  },
  {
    id: 'scalp_itching',
    type: 'choice',
    inputKey: 'coceira_couro',
    content: 'Você sente coceira ou irritação no couro cabeludo?',
    condition: (ctx) =>
      hasArea('cabelo')(ctx) &&
      (hasHairConcern('coceira')(ctx) ||
        hasHairConcern('caspa')(ctx) ||
        hasHairConcern('oleosidade')(ctx)),
    options: [
      { label: 'Sim, frequentemente', value: 'frequente' },
      { label: 'Às vezes', value: 'as_vezes' },
      { label: 'Não', value: 'nao' },
    ],
  },
  {
    id: 'family_history',
    type: 'choice',
    inputKey: 'historico_familiar',
    content: 'Existe histórico de alopecia / queda capilar na sua família?',
    condition: (ctx) => hasArea('cabelo')(ctx) && hasHairLossConcern(ctx),
    options: [
      { label: 'Sim', value: 'sim' },
      { label: 'Não', value: 'nao' },
      { label: 'Não sei', value: 'nao_sei' },
    ],
  },
  {
    id: 'family_history_who',
    type: 'choice',
    inputKey: 'historico_familiar_quem',
    multiple: true,
    minSelections: 1,
    content: 'Quem da família tem ou teve alopecia / queda capilar?',
    condition: (ctx) =>
      hasArea('cabelo')(ctx) && hasHairLossConcern(ctx) && ctx.vars.historico_familiar === 'sim',
    options: [
      { label: 'Pai', value: 'pai' },
      { label: 'Mãe', value: 'mae' },
      { label: 'Avô paterno', value: 'avo_paterno' },
      { label: 'Avô materno', value: 'avo_materno' },
      { label: 'Irmãos', value: 'irmaos' },
      { label: 'Tios', value: 'tios' },
    ],
  },
  {
    id: 'possible_triggers_female',
    type: 'choice',
    inputKey: 'possiveis_gatilhos',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Nenhum desses',
    content: 'Você passou por alguma dessas situações recentemente?',
    condition: (ctx) => hasArea('cabelo')(ctx) && hasHairLossConcern(ctx) && isFemale(ctx),
    options: [
      { label: 'Estresse intenso', value: 'estresse' },
      { label: 'Dieta restritiva / Perda de peso rápida', value: 'dieta' },
      { label: 'Cirurgia ou doença grave', value: 'doenca' },
      { label: 'Parto (últimos 12 meses)', value: 'parto' },
      { label: 'Mudança ou parada de anticoncepcional', value: 'anticoncepcional' },
      { label: 'COVID-19', value: 'covid' },
      { label: 'Menopausa ou alterações hormonais', value: 'hormonal' },
      {
        label: 'Uso de medicações para emagrecer (Ozempic, Wegovy, etc)',
        value: 'medicacao_emagrecer',
      },
    ],
  },
  {
    id: 'possible_triggers_male',
    type: 'choice',
    inputKey: 'possiveis_gatilhos',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Nenhum desses',
    content: 'Você passou por alguma dessas situações recentemente?',
    condition: (ctx) => hasArea('cabelo')(ctx) && hasHairLossConcern(ctx) && isMale(ctx),
    options: [
      { label: 'Estresse intenso', value: 'estresse' },
      { label: 'Dieta restritiva / Perda de peso rápida', value: 'dieta' },
      { label: 'Cirurgia ou doença grave', value: 'doenca' },
      { label: 'COVID-19', value: 'covid' },
      {
        label: 'Uso de medicações para emagrecer (Ozempic, Wegovy, etc)',
        value: 'medicacao_emagrecer',
      },
      { label: 'Uso de anabolizantes / testosterona', value: 'anabolizantes' },
    ],
  },
  {
    id: 'possible_triggers_neutral',
    type: 'choice',
    inputKey: 'possiveis_gatilhos',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Nenhum desses',
    content: 'Você passou por alguma dessas situações recentemente?',
    condition: (ctx) =>
      hasArea('cabelo')(ctx) && hasHairLossConcern(ctx) && !isMale(ctx) && !isFemale(ctx),
    options: [
      { label: 'Estresse intenso', value: 'estresse' },
      { label: 'Dieta restritiva / Perda de peso rápida', value: 'dieta' },
      { label: 'Cirurgia ou doença grave', value: 'doenca' },
      { label: 'COVID-19', value: 'covid' },
      {
        label: 'Uso de medicações para emagrecer (Ozempic, Wegovy, etc)',
        value: 'medicacao_emagrecer',
      },
      { label: 'Alterações hormonais', value: 'hormonal' },
    ],
  },
  {
    id: 'current_treatments',
    type: 'choice',
    inputKey: 'tratamentos_atuais_cabelo',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Não estou usando nada',
    content: 'Você já está usando algum tratamento para o cabelo?',
    condition: hasArea('cabelo'),
    options: [
      { label: 'Minoxidil', value: 'minoxidil' },
      { label: 'Finasterida / Dutasterida', value: 'finasterida' },
      { label: 'Shampoo anticaspa/antiqueda', value: 'shampoo' },
      { label: 'Suplementos (biotina, etc)', value: 'suplementos' },
      { label: 'Tratamento capilar em clínica', value: 'clinica' },
      { label: 'Transplante capilar', value: 'transplante' },
      { label: 'Outros', value: 'outros' },
    ],
  },
  {
    id: 'current_treatments_other',
    type: 'input',
    inputKey: 'tratamentos_atuais_cabelo_outros',
    content: 'Quais outros tratamentos você utiliza?',
    condition: (ctx) =>
      hasArea('cabelo')(ctx) && hasSelection('tratamentos_atuais_cabelo', 'outros')(ctx),
    validate: validateTextoLivre,
  },
  {
    id: 'treatment_duration',
    type: 'choice',
    inputKey: 'tratamento_duracao',
    content: 'Há quanto tempo você usa esse tratamento?',
    condition: (ctx) => hasArea('cabelo')(ctx) && hasHairTreatments(ctx),
    options: [
      { label: 'Menos de 3 meses', value: 'menos_3_meses' },
      { label: '3 a 6 meses', value: '3_6_meses' },
      { label: '6 meses a 1 ano', value: '6_meses_1_ano' },
      { label: 'Mais de 1 ano', value: 'mais_1_ano' },
    ],
  },
  {
    id: 'treatment_results',
    type: 'choice',
    inputKey: 'tratamento_resultados',
    content: 'Você notou algum resultado com o tratamento atual?',
    condition: (ctx) => hasArea('cabelo')(ctx) && hasHairTreatments(ctx),
    options: [
      { label: 'Sim, está funcionando', value: 'funcionando' },
      { label: 'Um pouco, mas não o esperado', value: 'parcial' },
      { label: 'Não vi diferença', value: 'sem_diferenca' },
      { label: 'Piorou', value: 'piorou' },
    ],
  },
  {
    id: 'intro_haircare',
    type: 'message',
    content: 'Agora algumas perguntas sobre seus hábitos de cuidado com o cabelo.',
    condition: hasArea('cabelo'),
  },
  {
    id: 'wash_frequency',
    type: 'choice',
    inputKey: 'frequencia_lavagem',
    content: 'Com que frequência você lava o cabelo?',
    condition: hasArea('cabelo'),
    options: [
      { label: 'Todos os dias', value: 'diario' },
      { label: 'Dia sim, dia não', value: 'alternado' },
      { label: '2-3 vezes por semana', value: '2_3_semana' },
      { label: '1 vez por semana ou menos', value: 'semanal' },
    ],
  },
  {
    id: 'hair_products',
    type: 'choice',
    inputKey: 'produtos_cabelo',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Só shampoo básico',
    content: 'Quais produtos você usa no cabelo?',
    condition: hasArea('cabelo'),
    options: [
      { label: 'Shampoo antiqueda', value: 'shampoo_antiqueda' },
      { label: 'Condicionador', value: 'condicionador' },
      { label: 'Máscara de tratamento', value: 'mascara' },
      { label: 'Leave-in / Sérum', value: 'leave_in' },
      { label: 'Tônico capilar', value: 'tonico' },
      { label: 'Óleo capilar', value: 'oleo' },
    ],
  },
  {
    id: 'heat_styling',
    type: 'choice',
    inputKey: 'uso_calor',
    content: 'Você usa secador, chapinha ou babyliss?',
    condition: hasArea('cabelo'),
    options: [
      { label: 'Sim, frequentemente', value: 'frequente' },
      { label: 'Às vezes', value: 'as_vezes' },
      { label: 'Raramente', value: 'raramente' },
      { label: 'Nunca', value: 'nunca' },
    ],
  },
  {
    id: 'chemical_treatments',
    type: 'choice',
    inputKey: 'quimica_cabelo',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Não faço química',
    content: 'Você faz algum tratamento químico no cabelo?',
    condition: hasArea('cabelo'),
    options: [
      { label: 'Tintura / Coloração', value: 'tintura' },
      { label: 'Descoloração', value: 'descoloracao' },
      { label: 'Progressiva / Alisamento', value: 'progressiva' },
      { label: 'Permanente', value: 'permanente' },
    ],
  },
  {
    id: 'tight_hairstyles',
    type: 'choice',
    inputKey: 'penteados_apertados',
    content: 'Você costuma prender o cabelo com muita força/tração?',
    condition: hasArea('cabelo'),
    options: [
      { label: 'Sim, frequentemente', value: 'frequente' },
      { label: 'Às vezes', value: 'as_vezes' },
      { label: 'Raramente', value: 'raramente' },
      { label: 'Não prendo o cabelo', value: 'nao_prende' },
      { label: 'Nunca', value: 'nunca' },
    ],
  },

  // ========== FACE ==========
  {
    id: 'intro_face',
    type: 'message',
    content: 'Agora vamos falar sobre a pele do seu rosto, {{nome}}.',
    condition: hasArea('pele_rosto'),
  },
  {
    id: 'skin_concerns_face',
    type: 'choice',
    inputKey: 'preocupacoes_pele',
    multiple: true,
    minSelections: 1,
    content: 'Quais são suas principais preocupações com a pele do rosto?',
    condition: hasArea('pele_rosto'),
    options: [
      { label: 'Acne / Cravos', value: 'acne' },
      { label: 'Manchas / Hiperpigmentação', value: 'manchas' },
      { label: 'Poros dilatados / Oleosidade', value: 'poros' },
      { label: 'Rugas / Linhas finas', value: 'rugas' },
      { label: 'Flacidez facial', value: 'flacidez' },
      { label: 'Textura irregular', value: 'textura_irregular' },
      { label: 'Falta de viço / Pele opaca', value: 'falta_de_vico' },
      { label: 'Ressecamento', value: 'ressecamento' },
    ],
  },
  {
    id: 'acne_severity',
    type: 'choice',
    inputKey: 'acne_gravidade',
    content: 'Como você descreveria sua acne?',
    condition: (ctx) => hasArea('pele_rosto')(ctx) && hasFaceConcern('acne')(ctx),
    options: [
      { label: 'Leve (alguns cravos ou espinhas)', value: 'leve' },
      { label: 'Moderada (várias lesões no rosto)', value: 'moderada' },
      { label: 'Severa (lesões inflamadas e dolorosas)', value: 'severa' },
    ],
  },
  {
    id: 'acne_location',
    type: 'choice',
    inputKey: 'acne_localizacao',
    multiple: true,
    minSelections: 1,
    content: 'Onde a acne aparece com mais frequência?',
    condition: (ctx) => hasArea('pele_rosto')(ctx) && hasFaceConcern('acne')(ctx),
    options: [
      { label: 'Testa', value: 'testa' },
      { label: 'Nariz', value: 'nariz' },
      { label: 'Bochechas', value: 'bochechas' },
      { label: 'Queixo', value: 'queixo' },
      { label: 'Mandíbula', value: 'mandibula' },
    ],
  },
  {
    id: 'acne_cycle_female',
    type: 'choice',
    inputKey: 'acne_ciclo',
    content: 'Sua acne costuma piorar perto do período menstrual?',
    condition: (ctx) => hasArea('pele_rosto')(ctx) && hasFaceConcern('acne')(ctx) && isFemale(ctx),
    options: [
      { label: 'Sim', value: 'sim' },
      { label: 'Não percebo relação', value: 'nao' },
    ],
  },
  {
    id: 'spots_type',
    type: 'choice',
    inputKey: 'manchas_tipo',
    multiple: true,
    minSelections: 1,
    content: 'Que tipo de manchas você tem?',
    condition: (ctx) => hasArea('pele_rosto')(ctx) && hasFaceConcern('manchas')(ctx),
    options: [
      { label: 'Manchas de sol', value: 'sol' },
      { label: 'Manchas de acne (marcas pós-inflamatórias)', value: 'pos_acne' },
      { label: 'Melasma', value: 'melasma' },
      { label: 'Sardas', value: 'sardas' },
      { label: 'Não sei ao certo', value: 'nao_sei' },
    ],
  },
  {
    id: 'pores_location',
    type: 'choice',
    inputKey: 'poros_localizacao',
    multiple: true,
    minSelections: 1,
    content: 'Onde os poros dilatados ou oleosidade incomodam mais?',
    condition: (ctx) => hasArea('pele_rosto')(ctx) && hasFaceConcern('poros')(ctx),
    options: [
      { label: 'Zona T (testa, nariz, queixo)', value: 'zona_t' },
      { label: 'Bochechas', value: 'bochechas' },
      { label: 'Rosto inteiro', value: 'rosto_inteiro' },
    ],
  },
  {
    id: 'wrinkles_location',
    type: 'choice',
    inputKey: 'rugas_localizacao',
    multiple: true,
    minSelections: 1,
    content: 'Onde as rugas ou linhas te incomodam mais?',
    condition: (ctx) => hasArea('pele_rosto')(ctx) && hasFaceConcern('rugas')(ctx),
    options: [
      { label: 'Testa', value: 'testa' },
      { label: 'Ao redor dos olhos (pés de galinha)', value: 'olhos' },
      { label: 'Entre as sobrancelhas', value: 'glabela' },
      { label: 'Ao redor da boca', value: 'boca' },
    ],
  },
  {
    id: 'dryness_level',
    type: 'choice',
    inputKey: 'ressecamento_intensidade',
    content: 'Como você descreve o ressecamento da pele do rosto?',
    condition: (ctx) => hasArea('pele_rosto')(ctx) && hasFaceConcern('ressecamento')(ctx),
    options: [
      { label: 'Leve', value: 'leve' },
      { label: 'Moderado', value: 'moderado' },
      { label: 'Intenso (descama fácil)', value: 'intenso' },
    ],
  },
  {
    id: 'eye_area_concerns',
    type: 'choice',
    inputKey: 'preocupacoes_area_dos_olhos',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Não tenho queixas',
    content: 'E na área dos olhos, o que mais te incomoda?',
    condition: hasArea('pele_rosto'),
    options: [
      { label: 'Rugas / Linhas finas', value: 'rugas_linhas_finas' },
      { label: 'Bolsas', value: 'bolsas' },
      { label: 'Olheiras', value: 'olheiras' },
    ],
  },
  {
    id: 'skin_type',
    type: 'choice',
    inputKey: 'tipo_de_pele',
    content: 'Qual seu tipo de pele do rosto?',
    condition: hasArea('pele_rosto'),
    options: [
      { label: 'Normal', value: 'normal' },
      { label: 'Seca', value: 'seca' },
      { label: 'Mista', value: 'mista' },
      { label: 'Oleosa', value: 'oleosa' },
    ],
  },
  {
    id: 'skin_tone',
    type: 'choice',
    inputKey: 'fototipo_pele',
    content: 'Qual seu fototipo?',
    condition: hasSkinArea,
    options: [
      { label: 'Extremamente clara (sempre queima, nunca bronzeia)', value: 'fitzpatrick_i' },
      { label: 'Clara (queima facilmente, bronzeia pouco)', value: 'fitzpatrick_ii' },
      { label: 'Morena clara / Amarelada (queima às vezes, bronzeia)', value: 'fitzpatrick_iii' },
      { label: 'Morena média (queima pouco, bronzeia com facilidade)', value: 'fitzpatrick_iv' },
      { label: 'Morena escura (raramente queima, bronzeia muito)', value: 'fitzpatrick_v' },
      { label: 'Escura (nunca queima, sempre bronzeia)', value: 'fitzpatrick_vi' },
    ],
  },
  {
    id: 'sensitive_skin',
    type: 'choice',
    inputKey: 'pele_sensivel',
    content: 'Você tem pele sensível ou alguma condição dermatológica?',
    condition: hasArea('pele_rosto'),
    options: [
      { label: 'Sim', value: 'sim' },
      { label: 'Não', value: 'nao' },
    ],
  },
  {
    id: 'sensitive_skin_condition',
    type: 'choice',
    inputKey: 'condicao_pele_sensivel',
    multiple: true,
    minSelections: 1,
    content: 'Quais condições você tem?',
    condition: (ctx) => hasArea('pele_rosto')(ctx) && ctx.vars.pele_sensivel === 'sim',
    options: [
      { label: 'Dermatite', value: 'dermatite' },
      { label: 'Rosácea', value: 'rosacea' },
      { label: 'Psoríase', value: 'psoriase' },
      { label: 'Eczema', value: 'eczema' },
      { label: 'Vitiligo', value: 'vitiligo' },
      { label: 'Outra', value: 'outra' },
    ],
  },
  {
    id: 'sensitive_skin_condition_other',
    type: 'input',
    inputKey: 'condicao_pele_sensivel_outra',
    content: 'Qual condição?',
    condition: (ctx) =>
      hasArea('pele_rosto')(ctx) &&
      ctx.vars.pele_sensivel === 'sim' &&
      hasSelection('condicao_pele_sensivel', 'outra')(ctx),
    validate: validateTextoLivre,
  },
  {
    id: 'sunscreen',
    type: 'choice',
    inputKey: 'protetor_solar_diario',
    content: 'Você usa protetor solar diariamente?',
    condition: hasSkinArea,
    options: [
      { label: 'Sim, sempre', value: 'sim' },
      { label: 'Às vezes', value: 'as_vezes' },
      { label: 'Não', value: 'nao' },
    ],
  },
  {
    id: 'sun_exposure',
    type: 'choice',
    inputKey: 'exposicao_sol',
    content: 'Com que frequência você se expõe ao sol?',
    condition: hasSkinArea,
    options: [
      { label: 'Diariamente', value: 'diariamente' },
      { label: 'Algumas vezes por semana', value: 'algumas_vezes' },
      { label: 'Raramente', value: 'raramente' },
    ],
  },
  {
    id: 'sun_protection_praise',
    type: 'message',
    content:
      'Muito bom, {{nome}}! Manter o protetor solar diário e atenção à exposição ao sol ajuda muito a pele.',
    condition: (ctx) =>
      hasSkinArea(ctx) &&
      isValue('protetor_solar_diario', 'sim')(ctx) &&
      isValue('exposicao_sol', 'diariamente')(ctx),
  },
  {
    id: 'moisturizer',
    type: 'choice',
    inputKey: 'hidratante',
    content: 'Você usa hidratante regularmente?',
    condition: hasSkinArea,
    options: [
      { label: 'Sim, rosto e corpo', value: 'sim_ambos' },
      { label: 'Só no rosto', value: 'so_rosto' },
      { label: 'Só no corpo', value: 'so_corpo' },
      { label: 'Não uso', value: 'nao' },
    ],
  },
  {
    id: 'regular_makeup',
    type: 'choice',
    inputKey: 'usa_maquiagem_regularmente',
    content: 'Você usa maquiagem regularmente?',
    condition: hasArea('pele_rosto'),
    options: [
      { label: 'Sim', value: 'sim' },
      { label: 'Às vezes', value: 'as_vezes' },
      { label: 'Não', value: 'nao' },
    ],
  },
  {
    id: 'skincare_products',
    type: 'choice',
    inputKey: 'produtos_skincare',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Não uso nenhum',
    content: 'Quais produtos você já usa na rotina de skincare?',
    condition: hasArea('pele_rosto'),
    options: [
      { label: 'Sabonete facial', value: 'sabonete_facial' },
      { label: 'Tônico', value: 'tonico' },
      { label: 'Sérum / Vitamina C', value: 'serum' },
      { label: 'Ácidos (retinol, glicólico, etc)', value: 'acidos' },
      { label: 'Antioxidantes', value: 'antioxidantes' },
      { label: 'Anti-idade', value: 'anti_idade' },
    ],
  },

  // ========== BODY ==========
  {
    id: 'intro_body',
    type: 'message',
    content: 'Agora vamos falar sobre a pele do seu corpo, {{nome}}.',
    condition: hasArea('pele_corpo'),
  },
  {
    id: 'body_concerns',
    type: 'choice',
    inputKey: 'preocupacoes_corpo',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Não tenho queixas no corpo',
    content: 'Você tem alguma preocupação com a pele do corpo?',
    condition: hasArea('pele_corpo'),
    options: [
      { label: 'Celulite', value: 'celulite' },
      { label: 'Estrias', value: 'estrias' },
      { label: 'Flacidez corporal', value: 'flacidez_corporal' },
      { label: 'Pele seca / Ressecamento', value: 'pele_seca' },
      { label: 'Acne corporal (costas, peito)', value: 'acne_corporal' },
      { label: 'Manchas no corpo', value: 'manchas_corpo' },
      { label: 'Queratose pilar (bolinhas nos braços)', value: 'queratose_pilar' },
      { label: 'Foliculite / Pelo encravado', value: 'foliculite' },
    ],
  },
  {
    id: 'cellulite_location',
    type: 'choice',
    inputKey: 'celulite_localizacao',
    multiple: true,
    minSelections: 1,
    content: 'Onde a celulite te incomoda mais?',
    condition: (ctx) => hasArea('pele_corpo')(ctx) && hasBodyConcern('celulite')(ctx),
    options: [
      { label: 'Coxas', value: 'coxas' },
      { label: 'Glúteos', value: 'gluteos' },
      { label: 'Barriga', value: 'barriga' },
      { label: 'Braços', value: 'bracos' },
    ],
  },
  {
    id: 'cellulite_severity',
    type: 'choice',
    inputKey: 'celulite_gravidade',
    content: 'Como você classifica sua celulite?',
    condition: (ctx) => hasArea('pele_corpo')(ctx) && hasBodyConcern('celulite')(ctx),
    options: [
      { label: 'Leve (só aparece quando aperto)', value: 'leve' },
      { label: 'Moderada (visível em pé)', value: 'moderada' },
      { label: 'Severa (muito visível, com ondulações)', value: 'severa' },
    ],
  },
  {
    id: 'stretch_marks_location',
    type: 'choice',
    inputKey: 'estrias_localizacao',
    multiple: true,
    minSelections: 1,
    content: 'Onde estão as estrias?',
    condition: (ctx) => hasArea('pele_corpo')(ctx) && hasBodyConcern('estrias')(ctx),
    options: [
      { label: 'Barriga', value: 'barriga' },
      { label: 'Coxas', value: 'coxas' },
      { label: 'Glúteos', value: 'gluteos' },
      { label: 'Seios', value: 'seios' },
      { label: 'Braços', value: 'bracos' },
      { label: 'Costas', value: 'costas' },
    ],
  },
  {
    id: 'stretch_marks_color',
    type: 'choice',
    inputKey: 'estrias_cor',
    content: 'Qual a cor das suas estrias?',
    condition: (ctx) => hasArea('pele_corpo')(ctx) && hasBodyConcern('estrias')(ctx),
    multiple: true,
    minSelections: 1,
    options: [
      { label: 'Vermelhas / Roxas (recentes)', value: 'vermelhas' },
      { label: 'Brancas / Prateadas (antigas)', value: 'brancas' },
    ],
  },
  {
    id: 'body_acne_location',
    type: 'choice',
    inputKey: 'acne_corporal_localizacao',
    multiple: true,
    minSelections: 1,
    content: 'Onde a acne corporal aparece?',
    condition: (ctx) => hasArea('pele_corpo')(ctx) && hasBodyConcern('acne_corporal')(ctx),
    options: [
      { label: 'Costas', value: 'costas' },
      { label: 'Peito', value: 'peito' },
      { label: 'Ombros', value: 'ombros' },
      { label: 'Glúteos', value: 'gluteos' },
    ],
  },
  {
    id: 'folliculitis_location',
    type: 'choice',
    inputKey: 'foliculite_localizacao',
    multiple: true,
    minSelections: 1,
    content: 'Onde a foliculite/pelo encravado aparece mais?',
    condition: (ctx) => hasArea('pele_corpo')(ctx) && hasBodyConcern('foliculite')(ctx),
    options: [
      { label: 'Virilha', value: 'virilha' },
      { label: 'Axilas', value: 'axilas' },
      { label: 'Pernas', value: 'pernas' },
      { label: 'Barba (rosto)', value: 'barba' },
      { label: 'Glúteos', value: 'gluteos' },
    ],
  },
  {
    id: 'body_spots_location',
    type: 'choice',
    inputKey: 'manchas_corpo_localizacao',
    multiple: true,
    minSelections: 1,
    content: 'Onde estão as manchas no corpo?',
    condition: (ctx) => hasArea('pele_corpo')(ctx) && hasBodyConcern('manchas_corpo')(ctx),
    options: [
      { label: 'Braços', value: 'bracos' },
      { label: 'Pernas', value: 'pernas' },
      { label: 'Costas', value: 'costas' },
      { label: 'Peito/colo', value: 'peito_colo' },
      { label: 'Barriga', value: 'barriga' },
    ],
  },

  // ========== NAILS ==========
  {
    id: 'intro_nails',
    type: 'message',
    content: 'Agora vamos falar sobre as unhas, {{nome}}.',
    condition: hasArea('unhas'),
  },
  {
    id: 'nails_concerns',
    type: 'choice',
    inputKey: 'preocupacoes_unhas',
    multiple: true,
    minSelections: 1,
    content: 'Quais são suas principais preocupações com as unhas?',
    condition: hasArea('unhas'),
    options: [
      { label: 'Unhas quebradiças', value: 'quebradicas' },
      { label: 'Unhas descamando', value: 'descamando' },
      { label: 'Manchas / Amareladas', value: 'manchas' },
      { label: 'Crescimento muito lento', value: 'crescimento_lento' },
      { label: 'Dor ou inflamação ao redor', value: 'dor_inflamacao' },
      { label: 'Unhas encravadas', value: 'encravadas' },
      { label: 'Suspeita de micose', value: 'micose' },
      { label: 'Cutículas muito ressecadas', value: 'cuticulas_ressecadas' },
    ],
  },
  {
    id: 'nails_location',
    type: 'choice',
    inputKey: 'unhas_localizacao',
    multiple: true,
    minSelections: 1,
    content: 'Em quais unhas você percebe mais o problema?',
    condition: (ctx) => hasArea('unhas')(ctx) && Array.isArray(ctx.vars.preocupacoes_unhas),
    options: [
      { label: 'Mãos', value: 'maos' },
      { label: 'Pés', value: 'pes' },
      { label: 'Ambos', value: 'ambos' },
    ],
  },
  {
    id: 'nails_aspect',
    type: 'choice',
    inputKey: 'unhas_aspecto',
    multiple: true,
    minSelections: 1,
    content: 'Como estão as unhas com manchas ou suspeita de micose?',
    condition: (ctx) =>
      hasArea('unhas')(ctx) && (hasNailConcern('manchas')(ctx) || hasNailConcern('micose')(ctx)),
    options: [
      { label: 'Amareladas', value: 'amareladas' },
      { label: 'Esbranquiçadas', value: 'esbranquiçadas' },
      { label: 'Mais grossas', value: 'grossas' },
      { label: 'Descolando da pele', value: 'descolando' },
    ],
  },
  {
    id: 'nails_pain',
    type: 'choice',
    inputKey: 'unhas_dor',
    content: 'Você sente dor, vermelhidão ou inflamação nas unhas?',
    condition: (ctx) => hasArea('unhas')(ctx) && hasNailConcern('dor_inflamacao')(ctx),
    options: [
      { label: 'Sim', value: 'sim' },
      { label: 'Às vezes', value: 'as_vezes' },
      { label: 'Não', value: 'nao' },
    ],
  },
  {
    id: 'nails_habits',
    type: 'choice',
    inputKey: 'habitos_unhas',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Nenhum desses',
    content: 'Algum desses hábitos faz parte da sua rotina?',
    condition: hasArea('unhas'),
    options: [
      { label: 'Roer unhas', value: 'roer' },
      { label: 'Retirar cutículas com frequência', value: 'retirar_cuticula' },
      { label: 'Contato frequente com produtos de limpeza', value: 'produtos_limpeza' },
      { label: 'Uso frequente de esmalte', value: 'esmalte' },
    ],
  },
  {
    id: 'nails_procedures',
    type: 'choice',
    inputKey: 'procedimentos_unhas',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Não faço',
    content: 'Você faz algum desses procedimentos?',
    condition: hasArea('unhas'),
    options: [
      { label: 'Unha em gel', value: 'gel' },
      { label: 'Fibra / Acrílico', value: 'fibra_acrilico' },
      { label: 'Lixamento frequente', value: 'lixamento' },
      { label: 'Spa/banho de parafina', value: 'spa' },
    ],
  },

  // ========== LIFESTYLE ==========
  {
    id: 'intro_lifestyle',
    type: 'message',
    content: 'Alguns hábitos podem influenciar a saúde do cabelo, pele e unhas, {{nome}}.',
  },
  {
    id: 'diet_quality',
    type: 'choice',
    inputKey: 'alimentacao',
    content: 'Como você avalia sua alimentação?',
    options: [
      { label: 'Equilibrada e variada', value: 'equilibrada' },
      { label: 'Poderia ser melhor', value: 'regular' },
      { label: 'Pobre em nutrientes', value: 'pobre' },
      { label: 'Faço dieta restritiva', value: 'restritiva' },
    ],
  },
  { ...sharedProteinIntakeStep },
  { ...sharedWaterIntakeStep },
  {
    id: 'water_intake_praise',
    type: 'message',
    content: 'Perfeito, {{nome}}! Hidratação adequada faz diferença na pele e nos fios.',
    condition: hasAdequateHydration,
  },
  { ...sharedActivityFrequencyStep },
  {
    id: 'sports_praise',
    type: 'message',
    content: 'Ótimo hábito, {{nome}}! A atividade física ajuda na saúde da pele, cabelo e unhas.',
    condition: isPhysicallyActive,
  },
  { ...sharedSleepQualityStep },
  { ...sharedStressLevelStep },
  {
    id: 'smoking',
    type: 'choice',
    inputKey: 'fumante',
    content: 'Você fuma?',
    options: [
      { label: 'Sim', value: 'sim' },
      { label: 'Não', value: 'nao' },
      { label: 'Parei recentemente', value: 'parou' },
    ],
  },
  {
    id: 'smoking_praise',
    type: 'message',
    content: 'Excelente, {{nome}}! Não fumar é um grande ponto positivo para a saúde da pele.',
    condition: isValue('fumante', 'nao'),
  },

  // ========== HEALTH CONDITIONS ==========
  {
    id: 'health_conditions_female',
    type: 'choice',
    inputKey: 'condicoes_saude',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Nenhuma dessas',
    content: 'Você tem alguma dessas condições?',
    condition: isFemale,
    options: [
      { label: 'Anemia', value: 'anemia' },
      { label: 'Problemas de tireoide', value: 'tireoide' },
      { label: 'Síndrome do ovário policístico (SOP)', value: 'sop' },
      { label: 'Diabetes', value: 'diabetes' },
      { label: 'Deficiência de vitaminas', value: 'vitaminas' },
      { label: 'Alopecia areata (falhas redondas)', value: 'alopecia_areata' },
      { label: 'Endometriose', value: 'endometriose' },
    ],
  },
  {
    id: 'health_conditions_other',
    type: 'choice',
    inputKey: 'condicoes_saude',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Nenhuma dessas',
    content: 'Você tem alguma dessas condições?',
    condition: (ctx) => !isFemale(ctx),
    options: [
      { label: 'Anemia', value: 'anemia' },
      { label: 'Problemas de tireoide', value: 'tireoide' },
      { label: 'Diabetes', value: 'diabetes' },
      { label: 'Deficiência de vitaminas', value: 'vitaminas' },
      { label: 'Alopecia areata (falhas redondas)', value: 'alopecia_areata' },
    ],
  },

  // ========== GOALS ==========
  {
    id: 'main_goals',
    type: 'choice',
    inputKey: 'objetivos_cabelos_pele_unhas',
    multiple: true,
    minSelections: 1,
    content: 'Quais são seus principais objetivos?',
    options: [
      { label: 'Reduzir a queda e fortalecer o cabelo', value: 'fortalecer_cabelo' },
      { label: 'Estimular crescimento capilar', value: 'crescimento_cabelo' },
      { label: 'Melhorar textura e viço da pele', value: 'melhorar_pele' },
      { label: 'Clarear manchas', value: 'clarear_manchas' },
      { label: 'Reduzir acne/oleosidade', value: 'reduzir_acne' },
      { label: 'Fortalecer unhas', value: 'fortalecer_unhas' },
    ],
  },
]
